import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Drawer,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardActions,
  IconButton,
  Grid,
  Snackbar,
  FormControlLabel,
  Switch,
  Chip,
  Pagination,
  InputAdornment,
  Stack,
  Tabs,
  Tab,
  Avatar,
  useMediaQuery,
  useTheme,
  LinearProgress,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Delete,
  Image as ImageIcon,
  Edit,
  Close,
  Search,
  FilterList,
  ArrowBack,
  Add,
  Videocam,
  UploadFile,
  PlayCircleOutline,
} from '@mui/icons-material';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListBannersQuery,
  useAddBannerMutation,
  useUpdateBannerMutation,
  useDeleteBannerMutation,
  Banner as BannerType,
} from '../../features/admin/bannerApi.ts';

// ─── constants ───────────────────────────────────────────────
const MAX_IMAGE_MB = 5;
const MAX_VIDEO_MB = 100;
const BANNER_LAYOUTS = ['hero', 'split', 'product-card', 'promo', 'wide', 'compact'];

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'];
const ACCEPTED_ALL_TYPES = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES];

const ACCEPTED_ATTR = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/webp',
  'video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska',
  '.jpg', '.jpeg', '.png', '.webp', '.mp4', '.mpeg', '.mov', '.avi', '.mkv',
].join(',');

// ─── helpers ─────────────────────────────────────────────────
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const isVideoFile = (file: File) => ACCEPTED_VIDEO_TYPES.includes(file.type);
const isImageFile = (file: File) => ACCEPTED_IMAGE_TYPES.includes(file.type);

// ─── component ───────────────────────────────────────────────
const Banner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Form states
  const [caption, setCaption] = useState('');
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [active, setActive] = useState(true);
  const [cta, setCta] = useState('');
  const [productId, setProductId] = useState('');
  const [mode, setMode] = useState<'main' | 'sub' | 'offer'>('main');
  const [layout, setLayout] = useState('hero');
  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);

  // Edit mode states
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<BannerType | null>(null);

  // Drag-over state for drop zone
  const [isDragOver, setIsDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // API hooks
  const { data, isLoading, error, isError, refetch } = useListBannersQuery();
  const [addBanner, { isLoading: isAdding }] = useAddBannerMutation();
  const [updateBanner, { isLoading: isUpdating }] = useUpdateBannerMutation();
  const [deleteBanner, { isLoading: isDeleting }] = useDeleteBannerMutation();

  const banners = data?.banners || [];
  const bannersPerPage = 8;

  // Filter by mode tab, then by search
  const filteredBanners = banners.filter((b) => {
    if (tabValue === 1 && b.mode !== 'main') return false;
    if (tabValue === 2 && b.mode !== 'sub') return false;
    if (tabValue === 3 && b.mode !== 'offer') return false;
    if (!searchStr.trim()) return true;
    const q = searchStr.toLowerCase();
    return (
      (b.title || '').toLowerCase().includes(q) ||
      (b.caption || '').toLowerCase().includes(q) ||
      (b.cta || '').toLowerCase().includes(q)
    );
  });

  const totalRecords = filteredBanners.length;
  const totalPages = Math.ceil(totalRecords / bannersPerPage);
  const paginatedBanners = filteredBanners.slice(
    (page - 1) * bannersPerPage,
    page * bannersPerPage
  );

  const isEditing = Boolean(editingBanner);
  const isBusy = isAdding || isUpdating;

  // ── image URL helper ────────────────────────────────────────
  const getMediaUrl = (banner: BannerType): string => {
    const candidates = ['banner_image', 'file_path', 'filename', 'image', 'file', 'path', 'image_path', 'banner_image_path'];
    let raw: string | null = null;
    for (const k of candidates) {
      const v = banner[k as keyof BannerType];
      if (v) { raw = v as string; break; }
    }
    if (!raw) return '';
    if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;

    // Extract filename from any path separators
    const parts = raw.split(/[/\\]/);
    const fname = parts[parts.length - 1];
    const base = (process.env.REACT_APP_SERVER_ENDPOINT || '').replace(/\/$/, '');
    return `${base}/images/${fname}`;
  };

  const getBannerMediaType = (banner: BannerType): 'video' | 'image' => {
    if (banner.media_type === 'video') return 'video';
    const url = getMediaUrl(banner).toLowerCase();
    if (url.match(/\.(mp4|mpeg|mov|avi|mkv)$/)) return 'video';
    return 'image';
  };

  // ── file selection / validation ─────────────────────────────
  const applyFile = (selected: File) => {
    if (!ACCEPTED_ALL_TYPES.includes(selected.type)) {
      showSnack('Unsupported file type. Use JPG, PNG, WebP, MP4, MOV, AVI, or MKV.', 'error');
      return;
    }

    const sizeMB = selected.size / (1024 * 1024);

    if (isVideoFile(selected) && sizeMB > MAX_VIDEO_MB) {
      showSnack(`Video too large (${formatFileSize(selected.size)}). Max: ${MAX_VIDEO_MB} MB.`, 'error');
      return;
    }

    if (isImageFile(selected) && sizeMB > MAX_IMAGE_MB) {
      showSnack(`Image too large (${formatFileSize(selected.size)}). Max: ${MAX_IMAGE_MB} MB.`, 'error');
      return;
    }

    // Revoke old preview URL to avoid memory leaks
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);

    setFile(selected);
    setFilePreviewUrl(URL.createObjectURL(selected));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) applyFile(selected);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const selected = e.dataTransfer.files?.[0];
    if (selected) applyFile(selected);
  };

  const clearFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setFile(null);
    setFilePreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── form lifecycle ──────────────────────────────────────────
  const resetForm = () => {
    setCaption('');
    setTitle('');
    setStartDate('');
    setEndDate('');
    setActive(true);
    setCta('');
    setProductId('');
    setMode('main');
    setLayout('hero');
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setFile(null);
    setFilePreviewUrl(null);
    setEditingBanner(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddBanner = () => { resetForm(); setShowForm(true); };

  const handleEditBanner = (banner: BannerType) => {
    setCaption(banner.caption || '');
    setTitle(banner.title || '');
    setStartDate(banner.start_date ? banner.start_date.split('T')[0] : '');
    setEndDate(banner.end_date ? banner.end_date.split('T')[0] : '');
    setActive(banner.active === 1 || banner.active === '1' || banner.active === true);
    setCta(banner.cta || '');
    setProductId(banner.product_id?.toString() || '');
    setMode(banner.mode === 'sub' ? 'sub' : banner.mode === 'offer' ? 'offer' : 'main');
    setLayout(banner.layout || 'hero');
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setFile(null);
    setFilePreviewUrl(null);
    setEditingBanner(banner);
    setShowForm(true);
  };

  const handleBackToList = () => { setShowForm(false); resetForm(); };

  // ── submit ──────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!caption.trim()) { showSnack('Please provide a caption', 'error'); return; }
    if (!title.trim()) { showSnack('Please provide a title', 'error'); return; }
    if (!startDate) { showSnack('Please provide a start date', 'error'); return; }
    if (!endDate) { showSnack('Please provide an end date', 'error'); return; }
    if (!cta.trim() && !productId) { showSnack('Please provide a CTA URL or a Product ID', 'error'); return; }
    if (!file && !isEditing) { showSnack('Please select an image or video', 'error'); return; }

    try {
      const formData = new FormData();
      formData.append('caption', caption);
      formData.append('title', title);
      formData.append('start_date', startDate);
      formData.append('end_date', endDate);
      formData.append('active', active ? '1' : '0');
      formData.append('cta', cta);
      formData.append('product_id', productId);
      formData.append('mode', mode);
      formData.append('layout', layout);
      if (file) formData.append('banner_image', file);

      if (isEditing && editingBanner) {
        await updateBanner({ id: editingBanner.id, formData }).unwrap();
        showSnack('Banner updated successfully!', 'success');
      } else {
        await addBanner(formData).unwrap();
        showSnack('Banner added successfully!', 'success');
      }

      handleBackToList();
      refetch();
    } catch (err: any) {
      showSnack(err?.data?.message || `Failed to ${isEditing ? 'update' : 'add'} banner`, 'error');
    }
  };

  const handleDeleteBanner = async (bannerId: number) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    try {
      await deleteBanner(bannerId).unwrap();
      showSnack('Banner deleted successfully!', 'success');
      refetch();
    } catch (err: any) {
      showSnack(err?.data?.message || 'Failed to delete banner', 'error');
    }
  };

  // ── misc helpers ─────────────────────────────────────────────
  const showSnack = (message: string, severity: 'success' | 'error') =>
    setSnackbar({ open: true, message, severity });

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));
  const handleChangePage = (_: React.ChangeEvent<unknown>, newPage: number) => setPage(newPage);

  const formatDate = (d: string) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const renderStatusChip = (isActive: boolean) =>
    isActive
      ? <Chip label="Active" size="small" sx={{ bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600, fontSize: '12px' }} />
      : <Chip label="Inactive" size="small" sx={{ bgcolor: '#e4e5e7', color: '#616161', fontWeight: 600, fontSize: '12px' }} />;

  // ── media type badge ─────────────────────────────────────────
  const MediaTypeBadge = ({ type }: { type: 'video' | 'image' }) => (
    <Chip
      icon={type === 'video' ? <Videocam sx={{ fontSize: '14px !important' }} /> : <ImageIcon sx={{ fontSize: '14px !important' }} />}
      label={type === 'video' ? 'Video' : 'Image'}
      size="small"
      sx={{
        bgcolor: type === 'video' ? '#e8f0fe' : '#fce8e6',
        color: type === 'video' ? '#1a73e8' : '#c5221f',
        fontWeight: 600,
        fontSize: '11px',
        height: 22,
        mt: 0.5,
      }}
    />
  );

  // ── mode badge (main vs sub vs offer placement) ───────────────
  const ModeBadge = ({ mode }: { mode?: string }) => {
    const config =
      mode === 'sub'
        ? { label: 'Sub', bg: '#fff4e5', color: '#b76e00' }
        : mode === 'offer'
        ? { label: 'Offer', bg: '#fde7f3', color: '#a3157a' }
        : { label: 'Main', bg: '#eef2ff', color: '#3730a3' };

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.bg,
          color: config.color,
          fontWeight: 600,
          fontSize: '11px',
          height: 22,
        }}
      />
    );
  };

  // ── thumbnail / preview in table ────────────────────────────
  const MediaThumb = ({ banner }: { banner: BannerType }) => {
    const url = getMediaUrl(banner);
    const type = getBannerMediaType(banner);

    if (!url) {
      return (
        <Avatar variant="rounded" sx={{ width: 60, height: 40, bgcolor: '#f1f2f4' }}>
          <ImageIcon />
        </Avatar>
      );
    }

    if (type === 'video') {
      return (
        <Box sx={{ position: 'relative', width: 60, height: 40, borderRadius: '6px', overflow: 'hidden', bgcolor: '#1a1a2e' }}>
          <video
            src={url}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            muted
            preload="metadata"
          />
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}>
            <PlayCircleOutline sx={{ color: 'white', fontSize: 20 }} />
          </Box>
        </Box>
      );
    }

    return (
      <Avatar
        src={url}
        variant="rounded"
        sx={{ width: 60, height: 40, bgcolor: '#f1f2f4' }}
      >
        <ImageIcon />
      </Avatar>
    );
  };

  // ── drop zone in form ────────────────────────────────────────
  const renderDropZone = () => {
    // New file selected
    if (file && filePreviewUrl) {
      const isVid = isVideoFile(file);
      return (
        <Box sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          {isVid ? (
            <Box sx={{ position: 'relative', width: 180, height: 100, borderRadius: '10px', overflow: 'hidden', bgcolor: '#000' }}>
              <video
                src={filePreviewUrl}
                controls
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            </Box>
          ) : (
            <Box
              component="img"
              src={filePreviewUrl}
              sx={{ width: 160, height: 90, objectFit: 'cover', borderRadius: '10px', display: 'block' }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {file.name} &nbsp;·&nbsp; {formatFileSize(file.size)}
          </Typography>
          <IconButton
            size="small"
            onClick={clearFile}
            sx={{
              position: 'absolute', top: -10, right: -10,
              bgcolor: 'white', boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              '&:hover': { bgcolor: '#fee2e2' },
            }}
          >
            <Close fontSize="small" sx={{ color: '#d32f2f' }} />
          </IconButton>
        </Box>
      );
    }

    // Editing and existing media present
    if (isEditing && editingBanner) {
      const existingUrl = getMediaUrl(editingBanner);
      const existingType = getBannerMediaType(editingBanner);
      if (existingUrl) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {existingType === 'video' ? (
              <Box sx={{ position: 'relative', width: 180, height: 100, borderRadius: '10px', overflow: 'hidden', bgcolor: '#000' }}>
                <video
                  src={existingUrl}
                  controls
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                />
              </Box>
            ) : (
              <Avatar src={existingUrl} variant="rounded" sx={{ width: 160, height: 90 }} />
            )}
            <Typography variant="caption" sx={{ color: '#6d7175' }}>
              Current {existingType} — click or drag to replace
            </Typography>
            <MediaTypeBadge type={existingType} />
          </Box>
        );
      }
    }

    // Empty state
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 56, height: 56, borderRadius: '12px',
          bgcolor: 'white', border: '1px solid #e1e3e5',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <UploadFile sx={{ fontSize: 26, color: '#6d7175' }} />
        </Box>
        <Box>
          <Button
            variant="outlined"
            size="small"
            sx={{
              textTransform: 'none', borderColor: '#c9cccf', color: '#202223',
              fontWeight: 500, borderRadius: '8px',
              '&:hover': { borderColor: '#202223', bgcolor: 'transparent' },
            }}
          >
            Add file
          </Button>
        </Box>
        <Typography variant="caption" sx={{ color: '#6d7175', textAlign: 'center', lineHeight: 1.6 }}>
          Images: JPG, PNG, WebP — max {MAX_IMAGE_MB} MB<br />
          Videos: MP4, MOV, AVI, MKV — max {MAX_VIDEO_MB} MB
        </Typography>
      </Box>
    );
  };

  // ── render ───────────────────────────────────────────────────
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh', 
      bgcolor: '#f1f2f4',
      overflow: 'hidden' // Prevent outer scroll
    }}>
      <Box sx={{ 
        display: 'flex', 
        flex: 1, 
        overflow: 'hidden' // Prevent flex container from scrolling
      }}>
        {/* Sidebar desktop */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main content area - FIXED SCROLLING */}
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden', // Important: hide overflow at this level
          height: '100%' // Take full height
        }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          
          {/* Scrollable content area - THIS IS WHERE SCROLLING HAPPENS */}
          <Box sx={{ 
            flexGrow: 1, 
            overflowY: 'auto', 
            overflowX: 'hidden',  // Enable scrolling only here
            backgroundColor: '#f1f2f4',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}>

            {/* ══════════════════ LIST VIEW ══════════════════ */}
            {!showForm ? (
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>Banners</Typography>
                  <Button
                    variant="contained"
                    onClick={handleAddBanner}
                    sx={{
                      bgcolor: '#202223', color: 'white', textTransform: 'none',
                      fontWeight: 600, borderRadius: '8px', px: 2.5,
                      '&:hover': { bgcolor: '#303030' },
                    }}
                  >
                    Add banner
                  </Button>
                </Box>

                <Card sx={{ 
                  borderRadius: '16px', 
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)', 
                  border: '1px solid #e1e3e5', 
                  overflow: 'hidden' // Keep card content contained
                }}>
                  {/* Tabs */}
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs
                      value={tabValue}
                      onChange={(_, v) => { setTabValue(v); setPage(1); }}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' },
                        '& .Mui-selected': { color: '#202223', fontWeight: 600 },
                        '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 },
                      }}
                    >
                      <Tab label={`All (${banners.length})`} />
                      {/* <Tab label={`Main (${banners.filter((b) => b.mode === 'main').length})`} />
                      <Tab label={`Sub (${banners.filter((b) => b.mode === 'sub').length})`} />
                      <Tab label={`Offer (${banners.filter((b) => b.mode === 'offer').length})`} /> */}
                      <Tab label={`1 `} />
                      <Tab label={`2`} />
                      <Tab label={`3 `} />
                    </Tabs>
                  </Box>

                  {/* Search + filter */}
                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Search banners"
                      value={searchStr}
                      onChange={(e) => { setSearchStr(e.target.value); setPage(1); }}
                      fullWidth
                      sx={{ maxWidth: 400, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<FilterList />}
                      sx={{
                        textTransform: 'none', borderColor: '#c9cccf', color: '#202223',
                        borderRadius: '8px', fontWeight: 500,
                        '&:hover': { borderColor: '#202223', bgcolor: 'transparent' },
                      }}
                    >
                      Filter
                    </Button>
                  </Box>

                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load banners. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoading && !isError && paginatedBanners.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        No banners found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr ? `No results for "${searchStr}"` : 'Get started by adding your first banner'}
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleAddBanner}
                        sx={{ bgcolor: '#202223', color: 'white', textTransform: 'none', fontWeight: 600, borderRadius: '8px', '&:hover': { bgcolor: '#303030' } }}
                      >
                        Add banner
                      </Button>
                    </Box>
                  )}

                  {!isLoading && paginatedBanners.length > 0 && (
                    <Box sx={{ 
                      bgcolor: 'white',
                      overflowX: 'hidden', // Enable horizontal scroll for table on small screens
                      '&::-webkit-scrollbar': {
                        height: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '4px',
                      },
                    }}>
                      
                      <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: '40px 80px 1fr 90px 80px 100px 1fr 120px 1fr 90px',
                      gap: 2,
                      px: 2,
                      py: 1.5,
                      borderBottom: '1px solid #e1e3e5',
                      bgcolor: '#fafbfb',
                    }}>
                      {['#', 'Preview', 'Title', 'Type', 'Mode', 'Product ID', 'Dates', 'Status', 'CTA', 'Actions'].map((h, i) => (
                        <Typography key={h} variant="caption" sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                          textAlign: i === 9 ? 'right' : 'left',
                        }}>
                          {h}
                        </Typography>
                      ))}
                    </Box>

                      {/* Table rows - same min-width */}
                      {paginatedBanners.map((banner: BannerType, index: number) => {
                        const isBannerActive = banner.active === 1 || banner.active === '1' || banner.active === true;
                        const mediaType = getBannerMediaType(banner);

                        return (
                         <Box
                          key={banner.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '40px 80px 1fr 90px 80px 100px 1fr 120px 1fr 90px',
                            gap: 2,
                            px: 2,
                            py: 2,
                            alignItems: 'center',
                            borderBottom: index < paginatedBanners.length - 1 ? '1px solid #e1e3e5' : 'none',
                            '&:hover': { bgcolor: '#f9fafb' },
                            transition: 'background-color 0.15s ease',
                          }}
                        >
                            <Typography variant="body2" sx={{ color: '#6d7175' }}>
                              {(page - 1) * bannersPerPage + index + 1}
                            </Typography>

                            <MediaThumb banner={banner} />

                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                {banner.title || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {banner.caption || 'No caption'}
                              </Typography>
                            </Box>

                            <MediaTypeBadge type={mediaType} />

                            <ModeBadge mode={banner.mode} />

                            <Box>
                              <Typography variant="body2" sx={{ color: '#202223', fontWeight: 500, fontSize: '14px' }}>
                                {banner.product_id || 'N/A'}
                              </Typography>
                              {banner.linked_prod_name && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {banner.linked_prod_name}
                                </Typography>
                              )}
                            </Box>

                            <Box>
                              <Typography variant="body2" sx={{ fontSize: '12px', color: '#202223' }}>
                                {formatDate(banner.start_date)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                to {formatDate(banner.end_date)}
                              </Typography>
                            </Box>

                            {renderStatusChip(isBannerActive)}

                            <Typography
                              variant="body2" color="text.secondary"
                              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '12px' }}
                            >
                              {banner.cta || 'No CTA'}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <IconButton size="small" onClick={() => handleEditBanner(banner)} sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}>
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteBanner(banner.id)}
                                disabled={isDeleting}
                                sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Card>

                {totalPages > 1 && !isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages} page={page} onChange={handleChangePage}
                      sx={{
                        '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 },
                        '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' },
                      }}
                    />
                  </Box>
                )}
              </Box>

            ) : (
              /* ══════════════════ FORM VIEW ══════════════════ */
              <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{ color: '#202223', textTransform: 'none', fontWeight: 500, mb: 2, '&:hover': { bgcolor: 'transparent' } }}
                  >
                    Banners
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    {isEditing ? 'Edit banner' : 'Add banner'}
                  </Typography>
                </Box>

                {isBusy && <LinearProgress sx={{ mb: 2, borderRadius: 4 }} />}

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    {/* Left column */}
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>

                        {/* Details card */}
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Banner details
                            </Typography>

                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#202223' }}>
                                Banner mode <Typography component="span" color="error">*</Typography>
                              </Typography>
                              <ToggleButtonGroup
                                value={mode}
                                exclusive
                                onChange={(_, v) => v && setMode(v)}
                                disabled={isBusy}
                                size="small"
                                sx={{
                                  '& .MuiToggleButton-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 2.5,
                                    color: '#6d7175',
                                    borderColor: '#c9cccf',
                                  },
                                  '& .Mui-selected': {
                                    bgcolor: '#202223 !important',
                                    color: 'white !important',
                                  },
                                }}
                              >
                                <ToggleButton value="main">Main banner</ToggleButton>
                                <ToggleButton value="sub">Sub banner</ToggleButton>
                                <ToggleButton value="offer">Offer banner</ToggleButton>
                              </ToggleButtonGroup>
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6d7175' }}>
                                Main banners appear in the primary/hero carousel; sub banners are shown in secondary placements; offer banners highlight promotions and deals.
                              </Typography>
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Banner layout</Typography>
                                <Select size="small" fullWidth value={layout} onChange={e => setLayout(e.target.value)} disabled={isBusy}>
                                  <MenuItem value="hero">Hero</MenuItem>
                                  <MenuItem value="split">Split</MenuItem>
                                  <MenuItem value="product-card">Product Card</MenuItem>
                                  <MenuItem value="promo">Promo</MenuItem>
                                  <MenuItem value="wide">Wide</MenuItem>
                                  <MenuItem value="compact">Compact</MenuItem>
                                </Select>
                                <Typography variant="caption" color="text.secondary">
                                  You can use multiple layouts under the same Main, Sub, or Offer banner type.
                                </Typography>
                              </Box>
                            </Box>

                            <TextField
                              label="Title" value={title} onChange={(e) => setTitle(e.target.value)}
                              fullWidth required placeholder="Enter banner title"
                              disabled={isBusy} sx={{ mb: 2 }}
                            />

                            <TextField
                              label="Caption" value={caption} onChange={(e) => setCaption(e.target.value)}
                              fullWidth required placeholder="Enter banner caption"
                              disabled={isBusy} sx={{ mb: 2 }}
                            />

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Start Date" type="date" value={startDate}
                                  onChange={(e) => setStartDate(e.target.value)}
                                  fullWidth required disabled={isBusy}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="End Date" type="date" value={endDate}
                                  onChange={(e) => setEndDate(e.target.value)}
                                  fullWidth required disabled={isBusy}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            </Grid>

                            <TextField
                              label="CTA URL" value={cta} onChange={(e) => setCta(e.target.value)}
                              fullWidth placeholder="https://example.com/product"
                              disabled={isBusy}
                              helperText="Enter the URL where users will be redirected when clicking the banner"
                            />

                            <Box sx={{ mt: 2 }}>
                              <TextField
                                label="Product ID" value={productId}
                                onChange={(e) => setProductId(e.target.value)}
                                fullWidth placeholder="Enter product ID (optional)"
                                disabled={isBusy} type="number"
                                helperText="Linked product details (name, price, discount and rating) are fetched dynamically and always stay in sync."
                              />
                            </Box>
                          </Box>
                        </Card>

                        {/* Media upload card */}
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>
                                Banner Media
                              </Typography>
                              <Stack direction="row" spacing={1}>
                                <Chip
                                  icon={<ImageIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Image"
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '11px', height: 22, borderColor: '#c9cccf', color: '#6d7175' }}
                                />
                                <Chip
                                  icon={<Videocam sx={{ fontSize: '14px !important' }} />}
                                  label="Video"
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '11px', height: 22, borderColor: '#c9cccf', color: '#6d7175' }}
                                />
                              </Stack>
                            </Box>

                            {/* Drop zone */}
                            <Box
                              onClick={() => !isBusy && fileInputRef.current?.click()}
                              onDragOver={(e) => { e.preventDefault(); if (!isBusy) setIsDragOver(true); }}
                              onDragLeave={() => setIsDragOver(false)}
                              onDrop={!isBusy ? handleDrop : undefined}
                              sx={{
                                border: `2px dashed ${isDragOver ? '#202223' : '#c9cccf'}`,
                                borderRadius: '12px',
                                p: 4,
                                textAlign: 'center',
                                cursor: isBusy ? 'not-allowed' : 'pointer',
                                bgcolor: isDragOver ? '#f0f0f0' : '#fafbfb',
                                transition: 'all 0.2s',
                                '&:hover': isBusy ? {} : { borderColor: '#202223', bgcolor: '#f6f6f7' },
                                opacity: isBusy ? 0.6 : 1,
                              }}
                            >
                              {renderDropZone()}
                            </Box>

                            {/* Hidden file input */}
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept={ACCEPTED_ATTR}
                              onChange={handleFileChange}
                              style={{ display: 'none' }}
                              disabled={isBusy}
                            />

                            {/* File type indicator when a file is selected */}
                            {file && (
                              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isVideoFile(file) ? (
                                  <Chip
                                    icon={<Videocam sx={{ fontSize: '14px !important' }} />}
                                    label={`Video selected · ${formatFileSize(file.size)}`}
                                    size="small"
                                    sx={{ bgcolor: '#e8f0fe', color: '#1a73e8', fontWeight: 600, fontSize: '11px' }}
                                  />
                                ) : (
                                  <Chip
                                    icon={<ImageIcon sx={{ fontSize: '14px !important' }} />}
                                    label={`Image selected · ${formatFileSize(file.size)}`}
                                    size="small"
                                    sx={{ bgcolor: '#fce8e6', color: '#c5221f', fontWeight: 600, fontSize: '11px' }}
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>

                    {/* Right column */}
                    <Grid item xs={12} md={4}>
                      <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                        <Box sx={{ p: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                            Status
                          </Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={active}
                                onChange={(e) => setActive(e.target.checked)}
                                disabled={isBusy}
                                color="primary"
                              />
                            }
                            label={active ? 'Active' : 'Inactive'}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {active ? 'Banner will be visible to users' : 'Banner will be hidden from users'}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Footer actions */}
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3, pt: 3, borderTop: '1px solid #e1e3e5' }}>
                    <Button
                      onClick={handleBackToList}
                      disabled={isBusy}
                      sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#f44336', borderRadius: '8px', px: 2.5 }}
                    >
                      Close
                    </Button>
                    <Button
                      onClick={handleBackToList}
                      disabled={isBusy}
                      sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#F78E12', borderRadius: '8px', px: 2.5 }}
                    >
                      Discard
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isBusy || !caption.trim() || !title.trim() || !startDate || !endDate || (!file && !isEditing)}
                      sx={{
                        bgcolor: '#008060', textTransform: 'none', fontWeight: 600,
                        borderRadius: '8px', px: 2.5,
                        '&:hover': { bgcolor: '#006e52' },
                      }}
                    >
                      {isBusy ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" />
                          {isEditing ? 'Updating...' : 'Adding...'}
                        </Box>
                      ) : (
                        isEditing ? 'Update banner' : 'Add banner'
                      )}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Banner;